"use client";

import { type CreateChannelInput, CreateChannelSchema } from "@gosaas/schemas";
import type { ConstantsNotificationChannelResponse } from "@gosaas/types";
import {
	formatNotificationChannelTypeLabel,
	NOTIFICATION_CHANNEL_TYPES_API,
	type NotificationChannelType,
} from "@gosaas/types/notification";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { apiClient, requestWithHandling } from "@/utils/api";

interface CreateNotificationChannelDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

export function CreateNotificationChannelDialog({
	open,
	onOpenChange,
	onSuccess,
}: CreateNotificationChannelDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { teamID } = useTeam();

	const form = useForm<CreateChannelInput>({
		resolver: zodResolver(CreateChannelSchema),
		defaultValues: {
			name: "",
			type: "email",
			email: "",
			url: "",
			secret: "",
		},
	});

	const channelType = form.watch("type");

	async function onSubmit(data: CreateChannelInput) {
		try {
			setIsLoading(true);
			const config: Record<string, string> = {};
			if (data.type === "email" && data.email) {
				config.email = data.email;
			} else if (data.type === "webhook" && data.url) {
				config.url = data.url;
				if (data.secret) config.secret = data.secret;
			}

			const response =
				await requestWithHandling<ConstantsNotificationChannelResponse>(() =>
					apiClient.api.teamNotificationsChannelsCreate(teamID as string, {
						name: data.name,
						type: data.type,
						config,
					}),
				);
			if (response.code !== "SUCCESS") {
				toast.error("Failed to create channel", {
					description: response.message,
				});
				return;
			}

			if (data.type === "email") {
				const isVerified = response.data?.is_verified;
				if (isVerified) {
					toast.success("Email channel created and auto-verified.");
				} else {
					toast.success(
						"Email channel created. Check your inbox to verify the email address.",
					);
				}
			} else {
				toast.success(
					"Webhook channel created. Click 'Send Test' to verify the endpoint.",
				);
			}

			form.reset();
			onOpenChange(false);
			onSuccess?.();
		} catch {
			toast.error("Couldn't create the channel", {
				description: "Try again or reload the page.",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[480px]">
				<DialogHeader>
					<DialogTitle>Add Notification Channel</DialogTitle>
					<DialogDescription>
						Configure an external channel to receive notifications.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Name</FormLabel>
									<FormControl>
										<Input placeholder="e.g. Ops Team Email" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="type"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Type</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Select channel type">
													{(value) =>
														typeof value === "string" && value.length > 0
															? formatNotificationChannelTypeLabel(
																	value as NotificationChannelType,
																)
															: null
													}
												</SelectValue>
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{NOTIFICATION_CHANNEL_TYPES_API.map((t) => (
												<SelectItem key={t} value={t}>
													{formatNotificationChannelTypeLabel(t)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						{channelType === "email" && (
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email Address</FormLabel>
										<FormControl>
											<Input
												type="email"
												placeholder="alerts@example.com"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{channelType === "webhook" && (
							<>
								<FormField
									control={form.control}
									name="url"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Webhook URL</FormLabel>
											<FormControl>
												<Input
													placeholder="https://hooks.example.com/notify"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="secret"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Secret (optional)</FormLabel>
											<FormControl>
												<Input
													type="password"
													placeholder="Used for HMAC-SHA256 signature"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? "Creating..." : "Create Channel"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
